import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Flag,
  CheckCircle,
  XCircle,
  Clock,
  Hourglass,
  User,
  ChalkboardTeacher,
  Camera,
  CaretDown,
  CaretUp,
  ClockClockwise,
  ChatCircleDots,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  listPhotoModerationQueue,
  moderateChildPhoto,
  moderationSignedUrl,
  listPhotoModerationHistory,
  loadModerationDigest,
  MODERATION_STATUSES,
  MODERATION_ACTION_META,
  moderationStatusMeta,
} from "@/lib/photoModeration";
import { findOrCreateDirectConversation } from "@/lib/messaging";
import { buildMentionToken } from "@/lib/mentions";
import { useAuth } from "@/context/AuthContext";
import { formatPhotoDate } from "@/lib/photos";

/**
 * Admin · /admin/photos/moderation
 *
 * Review queue for parent-flagged classroom photos. Three tabs:
 *   - Needs review  (status='flagged')   · default tab, actionable
 *   - Approved      (status='approved')  · recent admin approvals (audit)
 *   - Removed       (status='rejected')  · recent takedowns (audit)
 *
 * Each card shows the image, reporter info, and the admin actions.
 * Approve restores visibility for parents; Reject hides it from everyone
 * except admins (takedown). All state changes flow through
 * rpc_moderate_child_photo, which writes an append-only audit row.
 */
export default function AdminPhotoModeration() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);
  const [digest, setDigest] = useState(null);

  // Refresh the 7-day digest whenever the user navigates back here, when
  // they take a moderation action, or when the active tab changes.
  // Cheap query (one count + one bounded select), so over-fetching is fine.
  useEffect(() => {
    let alive = true;
    loadModerationDigest({ days: 7 })
      .then((d) => { if (alive) setDigest(d); })
      .catch(() => { if (alive) setDigest(null); });
    return () => { alive = false; };
  }, [status]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPhotoModerationQueue({ status, limit: 100 });
      setRows(data);
    } catch (err) {
      toast.error(err?.message || "Could not load queue");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  async function handleCoach(row) {
    if (!row.uploaded_by_id || !profile?.id || !profile?.organization_id) {
      toast.error("Uploader info unavailable");
      return;
    }
    try {
      const conv = await findOrCreateDirectConversation({
        organizationId: profile.organization_id,
        createdBy: profile.id,
        otherProfileId: row.uploaded_by_id,
        subject: "Photo coaching",
      });
      const teacherName = row.uploaded_by_name || "there";
      const childLabel = [row.child_first_name, row.child_last_name].filter(Boolean).join(" ");
      const reasonLine = row.report_reason
        ? `\n\nWhat was flagged: ${row.report_reason.replace(/\s+/g, " ").trim()}`
        : "";
      const mention = buildMentionToken({
        profileId: row.uploaded_by_id,
        name: teacherName,
      });
      const preface =
        `Hi ${mention || teacherName} — a parent flagged a photo` +
        (childLabel ? ` of ${childLabel}` : "") + "." + reasonLine +
        `\n\nCould we talk through it when you have a moment?`;
      navigate(`/admin/messaging/${conv.id}?prefill=${encodeURIComponent(preface)}`);
    } catch (err) {
      toast.error(err?.message || "Could not open a thread");
    }
  }

  async function handleAction(photoId, action) {
    setActioningId(photoId);
    try {
      await moderateChildPhoto(photoId, action);
      toast.success(action === "approve" ? "Photo approved" : "Photo removed");
      // Drop the row locally — it no longer matches the 'flagged' filter.
      setRows((prev) => prev.filter((r) => r.photo_id !== photoId));
    } catch (err) {
      toast.error(err?.message || "Could not update photo");
    } finally {
      setActioningId(null);
    }
  }

  const queueCounts = useMemo(() => {
    // Only the active-tab rows are in memory; counts pillar on the
    // active-tab length. Dashboard KPI uses rpc_photo_moderation_queue_count
    // for the authoritative flagged-count.
    return rows.length;
  }, [rows]);

  return (
    <div data-testid="admin-photo-moderation" className="space-y-8">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Photos
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Photo moderation
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Review classroom photos before parents see them. New teacher uploads
          land in <strong className="font-semibold text-neutral-800">Awaiting
          review</strong> — approve to release them to families, remove to
          take them down. Anything a parent reports later shows up under
          <strong className="font-semibold text-neutral-800"> Reported</strong>.
        </p>
      </header>

      {/* 7-day digest — calm operational pulse so admins can see at a
         glance what was reviewed this week and how big the backlog is. */}
      {digest && (digest.total_events > 0 || digest.pending_now > 0) && (
        <section
          data-testid="admin-photo-moderation-digest"
          className="rounded-2xl border border-neutral-200 bg-white p-4"
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-500">
              Last 7 days
            </p>
            <p className="text-[11px] text-neutral-500">
              Pending now ·{" "}
              <span
                data-testid="admin-photo-moderation-digest-pending"
                className="font-semibold text-neutral-900"
              >
                {digest.pending_now}
              </span>
            </p>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <DigestCell
              testId="admin-photo-moderation-digest-reported"
              label="Reported"
              value={digest.reported}
              tone="border-[#C6851E]/30 bg-[#C6851E]/5 text-[#C6851E]"
            />
            <DigestCell
              testId="admin-photo-moderation-digest-approved"
              label="Approved"
              value={digest.approved}
              tone="border-primary/30 bg-primary/5 text-primary-hover"
            />
            <DigestCell
              testId="admin-photo-moderation-digest-rejected"
              label="Removed"
              value={digest.rejected}
              tone="border-neutral-200 bg-neutral-50 text-neutral-700"
            />
          </div>
        </section>
      )}

      {/* Tabs */}
      <div
        data-testid="admin-photo-moderation-tabs"
        className="flex items-center gap-2 overflow-x-auto"
      >
        {MODERATION_STATUSES.map((s) => {
          const active = s.key === status;
          const Icon =
            s.key === "pending"
              ? Hourglass
              : s.key === "flagged"
                ? Flag
                : s.key === "approved"
                  ? CheckCircle
                  : XCircle;
          return (
            <button
              key={s.key}
              type="button"
              data-testid={`admin-photo-moderation-tab-${s.key}`}
              data-active={active}
              onClick={() => setStatus(s.key)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
                active
                  ? "border-primary/50 bg-primary/10 text-primary-hover"
                  : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              <Icon size={15} weight="duotone" />
              {s.label}
              {active && (
                <span
                  data-testid={`admin-photo-moderation-tab-count-${s.key}`}
                  className="ml-1 rounded-full bg-white px-2 text-[10px] font-bold text-neutral-700"
                >
                  {loading ? "…" : queueCounts}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState status={status} />
      ) : (
        <ul
          data-testid="admin-photo-moderation-list"
          className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
        >
          {rows.map((r) => (
            <ModerationCard
              key={r.photo_id}
              row={r}
              disabled={actioningId === r.photo_id}
              onApprove={() => handleAction(r.photo_id, "approve")}
              onReject={() => handleAction(r.photo_id, "reject")}
              onCoach={() => handleCoach(r)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptyState({ status }) {
  const copy = {
    pending:  { icon: Hourglass,   title: "Inbox is clear", note: "When teachers upload classroom photos they'll wait here for your approval." },
    flagged:  { icon: ShieldCheck, title: "Nothing reported", note: "When a parent reports a photo it will show up here." },
    approved: { icon: CheckCircle, title: "No approvals yet",  note: "Photos you approve will appear here for audit." },
    rejected: { icon: XCircle,     title: "No takedowns yet",  note: "Photos you remove will appear here for audit." },
  }[status] || { icon: ShieldCheck, title: "Nothing here", note: "" };
  const Icon = copy.icon;
  return (
    <div
      data-testid="admin-photo-moderation-empty"
      className="rounded-2xl border border-neutral-200 bg-white p-12 text-center"
    >
      <Icon size={32} weight="duotone" className="mx-auto text-neutral-400" />
      <p className="mt-3 font-heading text-base font-semibold text-neutral-900">
        {copy.title}
      </p>
      <p className="mt-1 text-sm text-neutral-500">{copy.note}</p>
    </div>
  );
}

function ModerationCard({ row, disabled, onApprove, onReject, onCoach }) {
  const [src, setSrc] = useState(null);
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    let alive = true;
    setErrored(false);
    (async () => {
      try {
        // Moderation card is small (~480px wide on desktop, less on mobile).
        // Server-side transform → ~30 KB instead of multi-MB originals.
        const u = await moderationSignedUrl(row.storage_path, { width: 640, quality: 70 });
        if (alive) setSrc(u);
      } catch {
        /* ignore */
      }
    })();
    return () => { alive = false; };
  }, [row.storage_path]);

  async function onImgError() {
    if (errored) return;
    setErrored(true);
    try {
      const original = await moderationSignedUrl(row.storage_path);
      if (original) setSrc(original);
    } catch {
      /* leave skeleton */
    }
  }

  const meta = moderationStatusMeta(row.moderation_status);
  const actionable = row.moderation_status === "pending" || row.moderation_status === "flagged";
  const isFlagged = row.moderation_status === "flagged";
  const isPending = row.moderation_status === "pending";
  const childName = [row.child_first_name, row.child_last_name].filter(Boolean).join(" ");

  return (
    <li
      data-testid={`admin-photo-moderation-card-${row.photo_id}`}
      data-status={row.moderation_status}
      className="overflow-hidden rounded-2xl border border-neutral-200 bg-white"
    >
      <div className="relative aspect-[4/3] bg-neutral-100">
        {src ? (
          <img
            src={src}
            alt={row.caption || "Flagged thumbnail"}
            loading="lazy"
            decoding="async"
            onError={onImgError}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full animate-pulse bg-neutral-100" />
        )}
        <span
          data-testid={`admin-photo-moderation-card-${row.photo_id}-pill`}
          className={`absolute left-3 top-3 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.tone}`}
        >
          {meta.label}
        </span>

        {/* "Previously reviewed" signal — only on flagged rows that
            already have at least one prior approve/reject event. Admins
            scan for this at a glance when triaging re-reports. */}
        {isFlagged && (row.prior_moderation_count || 0) > 0 && (
          <span
            data-testid={`admin-photo-moderation-card-${row.photo_id}-prior-pill`}
            data-prior-count={row.prior_moderation_count}
            title={
              row.last_prior_action
                ? `Last ${row.last_prior_action} by ${row.last_prior_actor_name || "admin"}`
                : "Reviewed previously"
            }
            className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-700 backdrop-blur-sm"
          >
            <ClockClockwise size={10} weight="bold" />
            Previously reviewed
          </span>
        )}
      </div>

      <div className="space-y-3 p-4">
        {row.caption && (
          <p className="line-clamp-2 text-sm text-neutral-800">{row.caption}</p>
        )}

        <dl className="space-y-1.5 text-xs text-neutral-600">
          <MetaRow icon={User} label="Child">
            {childName || "—"}
          </MetaRow>
          <MetaRow icon={ChalkboardTeacher} label="Classroom">
            {row.classroom_name || "—"}
          </MetaRow>
          <MetaRow icon={Camera} label="Uploaded by">
            {row.uploaded_by_name || "—"} · {formatPhotoDate(row.taken_at)}
          </MetaRow>
          {row.reported_at && (
            <MetaRow icon={Flag} label="Reported">
              {row.reported_by_name || "Parent"} · {formatPhotoDate(row.reported_at)}
            </MetaRow>
          )}
          {row.report_reason && (
            <div
              data-testid={`admin-photo-moderation-card-${row.photo_id}-reason`}
              className="rounded-2xl bg-[#C6851E]/5 p-2.5 text-[12px] leading-relaxed text-[#C0443A]"
            >
              <span className="font-semibold">Reason: </span>
              {row.report_reason}
            </div>
          )}
          {isPending && (
            <div
              data-testid={`admin-photo-moderation-card-${row.photo_id}-pending-note`}
              className="rounded-2xl bg-amber-50 p-2.5 text-[12px] leading-relaxed text-amber-900"
            >
              <span className="font-semibold">Awaiting review · </span>
              Hidden from parents until you approve it.
            </div>
          )}
          {!actionable && row.moderated_at && (
            <MetaRow icon={Clock} label="Moderated">
              {row.moderated_by_name || "Admin"} · {formatPhotoDate(row.moderated_at)}
            </MetaRow>
          )}
          {!actionable && row.moderation_note && (
            <p className="rounded-2xl bg-neutral-50 p-2.5 text-[12px] leading-relaxed text-neutral-700">
              <span className="font-semibold">Note: </span>
              {row.moderation_note}
            </p>
          )}
        </dl>

        {/* Inline "last prior decision" row — restrained, only for
            currently-flagged cards where there's genuinely prior history. */}
        {isFlagged && row.last_prior_action && (
          <p
            data-testid={`admin-photo-moderation-card-${row.photo_id}-prior-summary`}
            className="text-[11px] leading-relaxed text-neutral-500"
          >
            Last{" "}
            <strong className={`font-semibold ${MODERATION_ACTION_META[row.last_prior_action]?.tone || "text-neutral-700"}`}>
              {MODERATION_ACTION_META[row.last_prior_action]?.label?.toLowerCase() || row.last_prior_action}
            </strong>
            {row.last_prior_actor_name && <> by {row.last_prior_actor_name}</>}
            {row.last_prior_actor_at && <> · {formatPhotoDate(row.last_prior_actor_at)}</>}.
          </p>
        )}

        {/* Collapsible full moderation history. Renders only when there's
            something to show (>=1 event). On a first-time-flagged photo
            with no prior decisions, we hide the toggle entirely to keep
            the card clean. */}
        {((row.prior_moderation_count || 0) + (row.prior_report_count || 0)) > 1 && (
          <HistoryDisclosure photoId={row.photo_id} />
        )}

        {actionable && (
          <div className="space-y-2 border-t border-neutral-100 pt-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid={`admin-photo-moderation-card-${row.photo_id}-approve`}
                disabled={disabled}
                onClick={onApprove}
                className="flex-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:opacity-60"
              >
                {isPending ? "Approve & release" : "Approve"}
              </button>
              <button
                type="button"
                data-testid={`admin-photo-moderation-card-${row.photo_id}-reject`}
                disabled={disabled}
                onClick={onReject}
                className="flex-1 rounded-full border border-[#C6851E] bg-white px-4 py-2 text-sm font-semibold text-[#C6851E] transition hover:bg-[#C6851E]/5 disabled:opacity-60"
              >
                Remove
              </button>
            </div>
            {isFlagged && row.uploaded_by_id && onCoach && (
              <button
                type="button"
                data-testid={`admin-photo-moderation-card-${row.photo_id}-coach`}
                disabled={disabled}
                onClick={onCoach}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-[12px] font-semibold text-neutral-700 transition hover:bg-neutral-50 disabled:opacity-60"
              >
                <ChatCircleDots size={13} weight="duotone" />
                Coach {row.uploaded_by_name?.split(" ")[0] || "teacher"} in DM
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function MetaRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={13} weight="duotone" className="mt-0.5 flex-shrink-0 text-neutral-400" />
      <span className="font-semibold text-neutral-500">{label}:</span>
      <span className="min-w-0 flex-1 text-neutral-700">{children}</span>
    </div>
  );
}

/**
 * Collapsible per-photo moderation timeline. Lazy-loads on first open
 * so the queue render cost stays flat as history grows. Renders oldest
 * -> newest (RPC already orders asc) with a compact action pill + actor
 * + timestamp + optional note/reason.
 */
function HistoryDisclosure({ photoId }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && events === null) {
      setLoading(true);
      try {
        const list = await listPhotoModerationHistory(photoId);
        setEvents(list);
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <div data-testid={`admin-photo-moderation-card-${photoId}-history`}>
      <button
        type="button"
        data-testid={`admin-photo-moderation-card-${photoId}-history-toggle`}
        aria-expanded={open}
        onClick={toggle}
        className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11px] font-semibold text-neutral-700 transition hover:bg-neutral-50"
      >
        <ClockClockwise size={11} weight="duotone" />
        {open ? "Hide history" : "Show moderation history"}
        {open
          ? <CaretUp size={10} weight="bold" />
          : <CaretDown size={10} weight="bold" />
        }
      </button>
      {open && (
        <ol
          data-testid={`admin-photo-moderation-card-${photoId}-history-list`}
          className="mt-3 space-y-2 border-l-2 border-neutral-100 pl-3"
        >
          {loading && (
            <li className="text-[11px] text-neutral-500">Loading…</li>
          )}
          {!loading && (events || []).length === 0 && (
            <li className="text-[11px] text-neutral-500">No prior events.</li>
          )}
          {!loading && (events || []).map((ev) => {
            const meta = MODERATION_ACTION_META[ev.action] || { label: ev.action, tone: "text-neutral-700" };
            return (
              <li key={ev.event_id} className="relative">
                <span className="absolute -left-[17px] top-1.5 block h-2 w-2 rounded-full bg-neutral-300" />
                <p className="text-[11px] leading-snug">
                  <strong className={`font-semibold ${meta.tone}`}>{meta.label}</strong>
                  {ev.actor_name && <span className="text-neutral-600"> by {ev.actor_name}</span>}
                  <span className="text-neutral-400"> · {formatPhotoDate(ev.created_at)}</span>
                </p>
                {ev.note && (
                  <p className="mt-0.5 text-[11px] leading-snug text-neutral-500 whitespace-pre-wrap">
                    {ev.note}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}


function DigestCell({ testId, label, value, tone }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border px-3 py-2 ${tone}`}
    >
      <p className="font-heading text-xl font-semibold leading-none">{value}</p>
      <p className="mt-1 text-[10px] font-bold uppercase tracking-wider opacity-80">
        {label}
      </p>
    </div>
  );
}
